import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cars } from "@/data/cars";
import heroImage from "@/assets/ibiza-drive-hero.webp";
import driveImage from "@/assets/drive.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const IbizaDrive = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Drive Ibiza" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Ibiza · Season 2026
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Drive
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Prestige vehicles delivered directly to your location across Ibiza.
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Prestige Vehicles</span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              Travel Ibiza in prestige vehicles delivered directly to your location. 
              From iconic sports cars to refined SUVs, each vehicle is selected for its character, 
              comfort, and the experience it delivers on the island's most beautiful roads.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cars Grid */}
      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {cars.filter((car) => car.image || (car.gallery && car.gallery.length > 0)).map((car, i) => (
              <motion.div
                key={car.slug}
                className="group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 1, delay: (i % 2) * 0.1, ease }}
              >
                <Link to={`/drive/ibiza/${car.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <img
                      src={car.image || (car.gallery && car.gallery[0]) || ""}
                      alt={car.name}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-display-medium text-base md:text-lg text-foreground tracking-wide uppercase mb-1">
                    {car.name}
                  </h3>
                  <span className="font-label text-bronze text-xs block mb-3">{car.price}</span>
                  <p className="font-body text-muted-foreground text-sm leading-[1.8] mb-4">
                    {car.description}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
                    {car.specs.map((spec) => (
                      <span key={spec.label} className="font-body text-muted-foreground/60 text-xs">
                        {spec.label}: {spec.value}
                      </span>
                    ))}
                  </div>
                  <span className="font-label text-foreground/60 text-[10px] border-b border-foreground/20 pb-0.5 group-hover:text-foreground group-hover:border-foreground/50 transition-colors duration-700">
                    View details
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Vehicles Banner */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={driveImage} alt="Luxury driving in Ibiza" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-foreground/60" />
        </div>
        <div className="relative z-10 container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground mb-8 tracking-wide uppercase">
              Extended Fleet
            </h2>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-4">
              Beyond our current selection, a wider collection of prestige vehicles is available upon request.
            </p>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-4">
              Whether you're looking for a specific model, a convertible for the coast, or a vehicle for a special occasion — our team will source the perfect match.
            </p>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-10">
              Simply contact us, and we will present the possibilities.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-primary-foreground/40 text-primary-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            >
              Get in touch
            </a>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Vehicle Requests
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm mt-4">
              A simple process, designed with ease in mind.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                step: "1",
                title: "Choose your vehicle",
                text: "Browse our collection or tell us what you're looking for. Whether you have a specific car in mind or prefer guidance, our team will help you find the perfect match.",
              },
              {
                step: "2",
                title: "Share your dates",
                text: "Let us know your preferred dates and pick-up location. We'll check availability and arrange delivery directly to your villa, hotel, or the airport.",
              },
              {
                step: "3",
                title: "Enjoy the drive",
                text: "Your vehicle arrives fully prepared and ready. Take the keys, and let the island's most beautiful roads set the tone for your stay.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl md:text-6xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-lg text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          {(() => {
            const faqs = [
              { q: "How does booking a vehicle through Arzani work?", a: "After submitting your request, we provide available vehicle options based on your preferences. Once confirmed, the vehicle is reserved and delivered to your desired location." },
              { q: "Where can the vehicle be delivered?", a: "Vehicles can be delivered to airports, hotels, private residences, or other agreed locations." },
              { q: "What types of vehicles are available?", a: "We offer a curated selection of luxury and prestige vehicles, including sports cars, SUVs, and executive models." },
              { q: "What is required to rent a vehicle?", a: "A valid driving license and identification are required. Additional requirements may vary depending on the provider." },
              { q: "Is a deposit required?", a: "Yes. A deposit is required to confirm the reservation. Details will be provided during the booking process." },
              { q: "Can I request a specific vehicle model?", a: "Yes. We will always aim to source your preferred model, subject to availability." },
              { q: "Is insurance included?", a: "Basic insurance is typically included. Additional coverage options may be available upon request." },
              { q: "Can I book a chauffeur with the vehicle?", a: "Yes. Chauffeur-driven services can be arranged as part of Arzani Drive or through Arzani Lifestyle." },
              { q: "What happens in case of cancellation?", a: "Cancellation policies vary depending on the provider and will be communicated before confirmation." },
              { q: "Can vehicles be arranged last-minute?", a: "Yes, subject to availability. Our team will always aim to accommodate urgent requests." },
            ];
            const left = faqs.slice(0, 5);
            const right = faqs.slice(5);
            return (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease }}
              >
                <Accordion type="single" collapsible className="w-full">
                  {left.map((item, i) => (
                    <AccordionItem key={i} value={`faq-left-${i}`} className="border-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                <Accordion type="single" collapsible className="w-full">
                  {right.map((item, i) => (
                    <AccordionItem key={i} value={`faq-right-${i}`} className="border-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            );
          })()}

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, ease }}
          >
            <p className="font-body text-muted-foreground/60 text-sm mb-6">Still have a question?</p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section id="drive-inquire" className="section-spacing bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase mb-6">
              Request a Vehicle
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-10">
              Send us a message with your preferred vehicle, dates, and delivery location.
              Our team will respond promptly with availability and a tailored proposal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-muted-foreground/60 text-xs mt-8">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IbizaDrive;
